import {
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ProTable,
  type ActionType,
  type ProColumns,
} from '@ant-design/pro-components';
import { StorageBoxSpecsPage } from './StorageBoxSpecsPage';
import { StorageDeviceSpecsPage } from './StorageDeviceSpecsPage';
import { QualitySettingsPage } from './QualitySettingsPage';
import { SystemStatusPage } from './SystemStatusPage';
import { copyText } from '../lib/copyText';

const CATALOG_SOURCE_BANK_OPTIONS = [
  { value: 'ATCC', label: 'ATCC' },
  { value: 'DSMZ', label: 'DSMZ' },
  { value: 'ECACC', label: 'ECACC' },
  { value: 'JCRB', label: 'JCRB' },
  { value: 'RCB', label: 'RIKEN BRC' },
];
const DEFAULT_CATALOG_SOURCE_PRIORITY = [
  'ATCC',
  'DSMZ',
  'ECACC',
  'JCRB',
  'RCB',
];

type Role = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  permissionCodes: string[];
  version: number;
  isSystem: boolean;
};
type Assignment = {
  roleId: string;
  scopeType: string;
  scopeId?: string | null;
  effect: 'ALLOW' | 'DENY';
  validFrom?: string | null;
  validUntil?: string | null;
};
type Group = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  isSystem: boolean;
  version: number;
  memberCount: number;
  memberIds: string[];
  roleIds: string[];
  roleAssignments: Assignment[];
  permissionCodes: string[];
};
type Permission = {
  code: string;
  module: string;
  descriptionZh: string;
  descriptionEn: string;
  assignableGroupCodes: string[] | null;
};
type Rule = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  operationType: string;
  approverRoleId: string | null;
  requiresApproval: boolean;
  priority: number;
  version: number;
};
type User = {
  id: string;
  username: string;
  email: string | null;
  status: 'ACTIVE' | 'INACTIVE' | 'LOCKED';
  version: number;
  createdAt: string | null;
  lastLoginAt: string | null;
  roleIds: string[];
  roleAssignments: Assignment[];
  groupId: string | null;
  groupIds: string[];
};
type CreatedUserCredentials = {
  username: string;
  email: string;
  password: string;
};
type BoxSpec = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  rows: number;
  columns: number;
  isDefault?: boolean;
};
type DeviceSpec = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  deviceType: string;
  canisterCount: number;
  boxCount: number;
};
type CurrentUserSummary = {
  permissions: string[];
};
type GeneralSettings = {
  tenant: {
    id: string;
    code: string;
    nameZh: string | null;
    nameEn: string | null;
    systemName: string;
    systemNameZh: string;
    systemNameEn: string | null;
    domainName: string | null;
    logoUrl: string | null;
    defaultLocale: 'zh-CN' | 'en-US';
    timezone: string;
    contactEmail: string | null;
    version: number;
  };
  numberingRules: NumberingRule[];
  businessDefaults: {
    defaultSampleStatus: string;
    defaultStorageBoxSpecId: string | null;
    defaultStorageDeviceSpecId: string | null;
    defaultUserGroupId: string | null;
    defaultApprovalRulesEnabled: boolean;
  };
  securityPolicy: {
    passwordMinLength: number;
    loginMaxFailedAttempts: number;
    loginLockMinutes: number;
    sessionLifetimeHours: number;
    loginRateLimitPerMinute: number;
    passwordResetRequestRateLimitPerMinute: number;
    passwordResetConfirmRateLimitPerMinute: number;
    initialPasswordMustChange: boolean;
  };
  notificationSettings: {
    inAppEnabled: boolean;
    emailEnabled: boolean;
    retentionDays: number;
    emailApprovalEnabled: boolean;
    emailSecurityEnabled: boolean;
    emailSystemEnabled: boolean;
    email: {
      host: string | null;
      port: number;
      secure: boolean;
      requireTls: boolean;
      username: string | null;
      fromEmail: string | null;
      fromName: string | null;
      replyTo: string | null;
      passwordConfigured: boolean;
      password?: string;
    };
  };
  inventoryOperationSettings: Record<
    string,
    { enabled: boolean; userVisible: boolean }
  >;
  maintenanceSettings: {
    liquidNitrogenForecastEnabled: boolean;
  };
};
type NumberingRule = {
  entityType: string;
  prefix: string;
  dateFormat: string;
  serialLength: number;
  version: number;
};
type Kind = 'role' | 'group' | 'rule';
type Editable = Role | Group | Rule;
type AdminLoadPath =
  | 'general-settings'
  | 'roles'
  | 'permissions'
  | 'user-groups'
  | 'approval-rules'
  | 'users';
const operations = [
  'CONSUME',
  'LOAN',
  'RETURN',
  'MOVE',
  'DISPOSE',
  'QUARANTINE',
];
const inventoryOperations = [
  'PUTAWAY',
  'CONSUME',
  'LOAN',
  'RETURN',
  'MOVE',
  'DISPOSE',
  'QUARANTINE',
];
const adminLoadPaths: AdminLoadPath[] = [
  'general-settings',
  'roles',
  'permissions',
  'user-groups',
  'approval-rules',
  'users',
];
const numberingDateFormats = ['NONE', 'YYYY', 'YYYYMM', 'YYYYMMDD'];
const defaultSampleStatuses = ['DRAFT', 'AVAILABLE', 'QUARANTINED', 'MISSING'];
const passwordParts = {
  upper: 'ABCDEFGHJKLMNPQRSTUVWXYZ',
  lower: 'abcdefghijkmnopqrstuvwxyz',
  digit: '23456789',
};
class AdminLoadError extends Error {
  constructor(
    readonly path: AdminLoadPath,
    readonly status: number,
  ) {
    super(`Admin load failed for ${path} with status ${status}`);
  }
}

type AdminView = 'system' | 'users';

export function AdminPage({ view = 'system' }: { view?: AdminView }) {
  const { t, i18n } = useTranslation();
  const [generalSettings, setGeneralSettings] = useState<GeneralSettings>();
  const [logoSaving, setLogoSaving] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [catalogPrimarySource, setCatalogPrimarySource] = useState('ATCC');
  const [catalogFallbackSource, setCatalogFallbackSource] = useState('DSMZ');
  const [savedCatalogPrimarySource, setSavedCatalogPrimarySource] =
    useState('ATCC');
  const [savedCatalogFallbackSource, setSavedCatalogFallbackSource] =
    useState('DSMZ');
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUserSummary>();
  const [boxSpecs, setBoxSpecs] = useState<BoxSpec[]>([]);
  const [deviceSpecs, setDeviceSpecs] = useState<DeviceSpec[]>([]);
  const [templatesEnabled, setTemplatesEnabled] = useState(false);
  const [templateSettingLoading, setTemplateSettingLoading] = useState(false);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testEmailRecipient, setTestEmailRecipient] = useState('');
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState<Kind>();
  const [editing, setEditing] = useState<Editable>();
  const [selectedUser, setSelectedUser] = useState<User>();
  const [selectedUserForGroups, setSelectedUserForGroups] = useState<User>();
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [permissionMatrixOpen, setPermissionMatrixOpen] = useState(false);
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<string | null>(
    null,
  );
  const [groupPermissionDrafts, setGroupPermissionDrafts] = useState<
    Record<string, string[]>
  >({});
  const [userModal, setUserModal] = useState<'create' | 'reset'>();
  const [createdUser, setCreatedUser] = useState<CreatedUserCredentials>();
  const [savingUser, setSavingUser] = useState(false);
  const [form] = Form.useForm<Record<string, unknown>>();
  const [userForm] = Form.useForm<Record<string, unknown>>();
  const [generalForm] = Form.useForm<GeneralSettings>();
  const [messageApi, holder] = message.useMessage();
  const usersActionRef = useRef<ActionType | undefined>(undefined);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [systemTab, setSystemTab] = useState(
    () => searchParams.get('tab') ?? 'general',
  );
  const randomIndex = (max: number) => {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    return values[0]! % max;
  };
  const pickPasswordChar = (alphabet: string) =>
    alphabet[randomIndex(alphabet.length)];
  const passwordMinLength =
    generalSettings?.securityPolicy.passwordMinLength ?? 12;
  const generateInitialPassword = () => {
    const alphabet = `${passwordParts.upper}${passwordParts.lower}${passwordParts.digit}`;
    const chars = [
      pickPasswordChar(passwordParts.upper),
      pickPasswordChar(passwordParts.lower),
      pickPasswordChar(passwordParts.digit),
    ];
    while (chars.length < passwordMinLength)
      chars.push(pickPasswordChar(alphabet));
    for (let index = chars.length - 1; index > 0; index -= 1) {
      const swapIndex = randomIndex(index + 1);
      [chars[index], chars[swapIndex]] = [chars[swapIndex], chars[index]];
    }
    userForm.setFieldValue('password', chars.join(''));
  };
  const title = (x: { nameZh: string | null; nameEn: string | null }) =>
    i18n.resolvedLanguage === 'en-US'
      ? (x.nameEn ?? x.nameZh)
      : (x.nameZh ?? x.nameEn);
  const groupLabel = (group: Group) => title(group) ?? group.code;
  const roleLabel = (role: Role) => title(role) ?? role.code;
  const boxSpecLabel = (spec: BoxSpec) =>
    `${title(spec) ?? spec.code} (${spec.rows}×${spec.columns})`;
  const deviceSpecLabel = (spec: DeviceSpec) =>
    `${title(spec) ?? spec.code} (${spec.canisterCount}×${spec.boxCount})`;
  const permissionLabel = (permission: Permission) =>
    i18n.resolvedLanguage === 'en-US'
      ? (permission.descriptionEn ?? permission.descriptionZh)
      : (permission.descriptionZh ?? permission.descriptionEn);
  const permissionModuleLabel = (module: string) =>
    t(`admin.permissionModules.${module}`, { defaultValue: module });
  const numberingEntityTypeLabel = (entityType: string) =>
    t(`admin.numberingEntityTypes.${entityType}`, { defaultValue: entityType });
  const canManageStorage =
    currentUser?.permissions.includes('storage:manage') ?? false;
  const canManageIdentity =
    currentUser?.permissions.includes('identity:manage') ?? false;
  const canReadQuality =
    currentUser?.permissions.includes('qc:read') ||
    currentUser?.permissions.includes('qc:manage') ||
    false;
  const loadCurrentUser = async (): Promise<CurrentUserSummary> => {
    const response = await fetch('/api/v1/auth/me', { credentials: 'include' });
    if (response.status === 401) {
      window.location.assign('/login');
      throw new Error('Unauthorized while loading current user');
    }
    if (!response.ok) throw new Error('Load failed for current user');
    return response.json() as Promise<CurrentUserSummary>;
  };
  const loadJson = async (path: AdminLoadPath): Promise<unknown> => {
    const response = await fetch(`/api/v1/admin/${path}`, {
      credentials: 'include',
    });
    if (response.status === 401) {
      window.location.assign('/login');
      throw new AdminLoadError(path, response.status);
    }
    if (!response.ok) throw new AdminLoadError(path, response.status);
    return response.json();
  };
  const loadApiJson = async (path: string): Promise<unknown> => {
    const response = await fetch(`/api/v1/${path}`, {
      credentials: 'include',
    });
    if (response.status === 401) {
      window.location.assign('/login');
      throw new Error(`Unauthorized while loading ${path}`);
    }
    if (!response.ok) throw new Error(`Load failed for ${path}`);
    return response.json();
  };
  const load = async () => {
    setLoading(true);
    try {
      const nextCurrentUser = await loadCurrentUser();
      const nextCanManageStorage =
        nextCurrentUser.permissions.includes('storage:manage');
      const [payloads, nextBoxSpecs, nextDeviceSpecs, nextQcSettings] =
        await Promise.all([
          Promise.all(adminLoadPaths.map(loadJson)),
          view === 'system' && nextCanManageStorage
            ? loadApiJson('storage-box-specs')
            : [],
          view === 'system' && nextCanManageStorage
            ? loadApiJson('storage-device-specs')
            : [],
          view === 'system' &&
          (nextCurrentUser.permissions.includes('qc:read') ||
            nextCurrentUser.permissions.includes('qc:manage'))
            ? loadApiJson('qc-settings')
            : null,
        ]);
      setCurrentUser(nextCurrentUser);
      const loadedGeneralSettings = payloads[0] as GeneralSettings;
      setRoles((payloads[1] as { items: Role[] }).items);
      setPermissions((payloads[2] as { items: Permission[] }).items);
      const nextGroups = (payloads[3] as { items: Group[] }).items;
      const loadedBoxSpecs = nextBoxSpecs as BoxSpec[];
      const defaultBoxSpecId =
        loadedGeneralSettings.businessDefaults.defaultStorageBoxSpecId ??
        loadedBoxSpecs.find((spec) => spec.isDefault)?.id ??
        loadedBoxSpecs[0]?.id ??
        null;
      const defaultUserGroupId =
        loadedGeneralSettings.businessDefaults.defaultUserGroupId ??
        nextGroups.find(
          (group) => group.code === 'USER' && group.status === 'ACTIVE',
        )?.id ??
        nextGroups.find((group) => group.status === 'ACTIVE')?.id ??
        null;
      const nextGeneralSettings: GeneralSettings = {
        ...loadedGeneralSettings,
        businessDefaults: {
          ...loadedGeneralSettings.businessDefaults,
          defaultStorageBoxSpecId: defaultBoxSpecId,
          defaultUserGroupId,
        },
      };
      setGeneralSettings(nextGeneralSettings);
      generalForm.setFieldsValue(nextGeneralSettings);
      setGroups(nextGroups);
      setGroupPermissionDrafts(
        Object.fromEntries(
          nextGroups.map((group) => [group.id, group.permissionCodes]),
        ),
      );
      setRules((payloads[4] as { items: Rule[] }).items);
      setUsers((payloads[5] as { items: User[] }).items);
      void usersActionRef.current?.reload();
      setBoxSpecs(loadedBoxSpecs);
      setDeviceSpecs(nextDeviceSpecs as DeviceSpec[]);
      setTemplatesEnabled(
        (nextQcSettings as { templatesEnabled: boolean } | null)
          ?.templatesEnabled ?? false,
      );
    } catch (error) {
      if (error instanceof AdminLoadError) {
        const source = t(`admin.loadSources.${error.path}`);
        console.error(error);
        await messageApi.error(
          t(
            error.status === 403
              ? 'admin.loadForbidden'
              : 'admin.loadFailedWithSource',
            { source, status: error.status },
          ),
        );
      } else {
        console.error(error);
        await messageApi.error(t('admin.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, [view]);
  useEffect(() => {
    const requestedTab = searchParams.get('tab') ?? 'general';
    if (
      view === 'system' &&
      (requestedTab === 'maintenance' || requestedTab === 'liquid-nitrogen')
    ) {
      void navigate('/maintenance', { replace: true });
      return;
    }
    setSystemTab(requestedTab);
  }, [navigate, searchParams, view]);
  useEffect(() => {
    if (view !== 'users' || searchParams.get('action') !== 'create') return;
    userForm.resetFields();
    setUserModal('create');
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, userForm, view]);
  useEffect(() => {
    void (async () => {
      try {
        const response = await fetch(
          '/api/v1/admin/cell-line-catalog/source-preference',
          { credentials: 'include' },
        );
        if (!response.ok) return;
        const payload = (await response.json()) as {
          cultureSourcePriority: string[];
        };
        if (Array.isArray(payload.cultureSourcePriority)) {
          setCatalogPrimarySource(payload.cultureSourcePriority[0] ?? 'ATCC');
          setCatalogFallbackSource(payload.cultureSourcePriority[1] ?? 'DSMZ');
          setSavedCatalogPrimarySource(
            payload.cultureSourcePriority[0] ?? 'ATCC',
          );
          setSavedCatalogFallbackSource(
            payload.cultureSourcePriority[1] ?? 'DSMZ',
          );
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);
  const changeSystemTab = (key: string) => {
    setSystemTab(key);
    setSearchParams(key === 'general' ? {} : { tab: key });
  };
  const saveCatalogSourcePriority = async (
    primary: string,
    fallback: string,
  ) => {
    const priority = [
      primary,
      fallback,
      ...DEFAULT_CATALOG_SOURCE_PRIORITY.filter(
        (bank) => bank !== primary && bank !== fallback,
      ),
    ];
    try {
      const response = await fetch(
        '/api/v1/admin/cell-line-catalog/source-preference',
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ cultureSourcePriority: priority }),
        },
      );
      if (!response.ok) throw new Error('save failed');
      const payload = (await response.json().catch(() => null)) as {
        changedRecords?: number;
      } | null;
      setCatalogPrimarySource(primary);
      setCatalogFallbackSource(fallback);
      setSavedCatalogPrimarySource(primary);
      setSavedCatalogFallbackSource(fallback);
      await messageApi.success(
        t('admin.catalogSourcePriorityApplied', {
          count: payload?.changedRecords ?? 0,
        }),
      );
    } catch (error) {
      console.error(error);
      await messageApi.error(t('admin.catalogSaveFailed'));
    }
  };
  const updateTemplateSetting = async (checked: boolean) => {
    setTemplateSettingLoading(true);
    try {
      const response = await fetch('/api/v1/qc-settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ templatesEnabled: checked }),
      });
      if (!response.ok) {
        await messageApi.error(t('quality.settingsSaveFailed'));
        return;
      }
      setTemplatesEnabled(checked);
      await messageApi.success(t('quality.settingsSaved'));
    } finally {
      setTemplateSettingLoading(false);
    }
  };
  const save = async (values: Record<string, unknown>) => {
    if (!kind) return;
    const path =
      kind === 'role'
        ? 'roles'
        : kind === 'group'
          ? 'user-groups'
          : 'approval-rules';
    const body = {
      ...values,
      ...(kind === 'role'
        ? {
            permissionCodes: ((values.permissionCodes as string) ?? '')
              .split(',')
              .map((x) => x.trim())
              .filter(Boolean),
          }
        : {}),
      ...(kind === 'rule'
        ? {
            requiresApproval: Boolean(values.requiresApproval),
            priority: Number(values.priority ?? 100),
            approverRoleId: values.requiresApproval
              ? values.approverRoleId
              : null,
          }
        : {}),
    };
    const response = await fetch(
      `/api/v1/admin/${path}${editing ? `/${editing.id}` : ''}`,
      {
        method: editing ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          editing ? { ...body, version: editing.version } : body,
        ),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('admin.saveFailed'));
      return;
    }
    setKind(undefined);
    setEditing(undefined);
    await load();
    await messageApi.success(t('admin.saved'));
  };
  const saveGeneralSettings = async (values: GeneralSettings) => {
    const response = await fetch('/api/v1/admin/general-settings', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    });
    if (!response.ok) {
      const detail = await response.text();
      console.error(
        'General settings save failed',
        JSON.stringify({ status: response.status, detail }),
      );
      let reason: string | undefined;
      try {
        reason = (JSON.parse(detail) as { detail?: string }).detail;
      } catch {
        reason = undefined;
      }
      await messageApi.error(
        reason === 'Organization code already exists'
          ? t('admin.tenantCodeExists')
          : reason ?? t('admin.saveFailed'),
      );
      return;
    }
    const nextSettings = (await response.json()) as GeneralSettings;
    setGeneralSettings(nextSettings);
    generalForm.setFieldsValue(nextSettings);
    window.dispatchEvent(new Event('tenant:branding-changed'));
    void messageApi.success(t('admin.saved'));

    const catalogSourcesChanged =
      catalogPrimarySource !== savedCatalogPrimarySource ||
      catalogFallbackSource !== savedCatalogFallbackSource;
    if (catalogSourcesChanged) {
      await saveCatalogSourcePriority(
        catalogPrimarySource,
        catalogFallbackSource,
      );
    }
  };
  const saveLogo = async (file?: File) => {
    if (!generalSettings) return;
    if (
      file &&
      (!['image/png', 'image/jpeg'].includes(file.type) ||
        file.size > 2 * 1024 * 1024)
    ) {
      await messageApi.error(t('admin.logoInvalidFile'));
      return;
    }
    setLogoSaving(true);
    try {
      const body = new FormData();
      if (file) body.append('file', file);
      if (file) body.append('version', String(generalSettings.tenant.version));
      const response = await fetch('/api/v1/admin/logo', {
        method: file ? 'POST' : 'DELETE',
        credentials: 'include',
        ...(file
          ? { body }
          : {
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({ version: generalSettings.tenant.version }),
            }),
      });
      if (!response.ok) {
        const error = (await response.json().catch(() => ({}))) as {
          detail?: string;
        };
        await messageApi.error(error.detail ?? t('admin.saveFailed'));
        return;
      }
      const nextSettings = (await response.json()) as GeneralSettings;
      setGeneralSettings(nextSettings);
      const currentValues = generalForm.getFieldsValue(true) as GeneralSettings;
      generalForm.setFieldsValue({
        tenant: {
          ...currentValues.tenant,
          logoUrl: nextSettings.tenant.logoUrl,
          version: nextSettings.tenant.version,
        },
      });
      window.dispatchEvent(new Event('tenant:branding-changed'));
      await messageApi.success(t('admin.saved'));
    } catch {
      await messageApi.error(t('admin.saveFailed'));
    } finally {
      setLogoSaving(false);
    }
  };
  const testEmail = async () => {
    setTestEmailLoading(true);
    try {
      const response = await fetch('/api/v1/admin/email/test', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          ...(testEmailRecipient.trim()
            ? { to: testEmailRecipient.trim() }
            : {}),
        }),
      });
      if (!response.ok) {
        const error = (await response.json().catch(() => ({}))) as {
          detail?: string;
        };
        const knownErrors: Record<string, string> = {
          'A valid test recipient email is required': t(
            'admin.emailTestRecipientRequired',
          ),
          'SMTP server is not configured': t('admin.emailTestServerMissing'),
          'SMTP sender email is not configured': t(
            'admin.emailTestSenderMissing',
          ),
        };
        await messageApi.error(
          error.detail
            ? (knownErrors[error.detail] ?? error.detail)
            : t('admin.emailTestFailed'),
        );
        return;
      }
      await messageApi.success(t('admin.emailTestQueued'));
    } catch {
      await messageApi.error(t('admin.emailTestFailed'));
    } finally {
      setTestEmailLoading(false);
    }
  };
  const saveUser = async (values: Record<string, unknown>) => {
    if (!userModal) return;
    const isCreating = userModal === 'create';
    const route =
      isCreating
        ? 'users'
        : `users/${selectedUser?.id}/reset-password`;
    const payload =
      isCreating
        ? values
        : { password: values.password, version: selectedUser?.version };
    setSavingUser(true);
    try {
      const response = await fetch(`/api/v1/admin/${route}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        await messageApi.error(t('admin.saveFailed'));
        return;
      }
      if (isCreating) {
        const created = (await response.json().catch(() => null)) as {
          username?: string;
        } | null;
        const email = String(values.email).trim().toLowerCase();
        const username =
          created?.username ||
          (typeof values.username === 'string' && values.username.trim()) ||
          email.split('@')[0] ||
          email;
        setCreatedUser({
          username,
          email,
          password: String(values.password),
        });
      }
      setUserModal(undefined);
      userForm.resetFields();
      await load();
      if (!isCreating) await messageApi.success(t('admin.saved'));
    } catch {
      await messageApi.error(t('admin.saveFailed'));
    } finally {
      setSavingUser(false);
    }
  };
  const copyCreatedUserInfo = async () => {
    if (!createdUser) return;
    const details = [
      `${t('admin.username')}: ${createdUser.username}`,
      `${t('admin.email')}: ${createdUser.email}`,
      `${t('admin.password')}: ${createdUser.password}`,
    ].join('\n');
    try {
      await copyText(details);
      await messageApi.success(t('admin.userInfoCopied'));
    } catch {
      await messageApi.error(t('admin.userInfoCopyFailed'));
    }
  };
  const updateStatus = async (user: User) => {
    const status = user.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    const response = await fetch(`/api/v1/admin/users/${user.id}/status`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version: user.version, status }),
    });
    if (!response.ok) {
      await messageApi.error(t('admin.saveFailed'));
      return;
    }
    await load();
    await messageApi.success(t('admin.saved'));
  };
  const deleteSelectedUsers = async () => {
    const targets = users.filter((user) => selectedUserIds.includes(user.id));
    for (const user of targets) {
      const response = await fetch(`/api/v1/admin/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ version: user.version }),
      });
      if (!response.ok) {
        await messageApi.error(t('admin.deleteFailed'));
        await load();
        return;
      }
    }
    setSelectedUserIds([]);
    await load();
    await messageApi.success(t('admin.deleted'));
  };
  const saveUserGroups = async () => {
    if (!selectedUserForGroups) return;
    const response = await fetch(
      `/api/v1/admin/users/${selectedUserForGroups.id}/groups`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          version: selectedUserForGroups.version,
          groupId: selectedUserGroupId,
        }),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('admin.saveFailed'));
      return;
    }
    setSelectedUserForGroups(undefined);
    await load();
    await messageApi.success(t('admin.saved'));
  };
  const toggleGroupPermission = (
    groupId: string,
    permissionCode: string,
    checked: boolean,
  ) => {
    setGroupPermissionDrafts((drafts) => {
      const current = new Set(drafts[groupId] ?? []);
      if (checked) current.add(permissionCode);
      else current.delete(permissionCode);
      return { ...drafts, [groupId]: [...current].sort() };
    });
  };
  const hasPermissionMatrixChanges = groups.some((group) => {
    const draft = [...(groupPermissionDrafts[group.id] ?? [])].sort();
    const saved = [...group.permissionCodes].sort();
    return draft.join('\n') !== saved.join('\n');
  });
  const resetPermissionMatrix = () => {
    setGroupPermissionDrafts(
      Object.fromEntries(
        groups.map((group) => [group.id, group.permissionCodes]),
      ),
    );
  };
  const closePermissionMatrix = () => {
    resetPermissionMatrix();
    setPermissionMatrixOpen(false);
  };
  const savePermissionMatrix = async () => {
    const changedGroups = groups.filter((group) => {
      const draft = [...(groupPermissionDrafts[group.id] ?? [])].sort();
      const saved = [...group.permissionCodes].sort();
      return draft.join('\n') !== saved.join('\n');
    });
    for (const group of changedGroups) {
      const response = await fetch(
        `/api/v1/admin/user-groups/${group.id}/permissions`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            version: group.version,
            permissionCodes: groupPermissionDrafts[group.id] ?? [],
          }),
        },
      );
      if (!response.ok) {
        await messageApi.error(t('admin.saveFailed'));
        return;
      }
    }
    await load();
    setPermissionMatrixOpen(false);
    await messageApi.success(t('admin.saved'));
  };
  const open = (next: Kind) => {
    form.resetFields();
    if (next === 'rule')
      form.setFieldsValue({
        requiresApproval:
          generalSettings?.businessDefaults.defaultApprovalRulesEnabled ?? true,
        priority: 100,
      });
    setKind(next);
  };
  const edit = (x: Editable, next: Kind) => {
    form.setFieldsValue(
      next === 'role'
        ? {
            code: x.code,
            nameZh: x.nameZh ?? undefined,
            nameEn: x.nameEn ?? undefined,
            permissionCodes: (x as Role).permissionCodes.join(', '),
          }
        : next === 'group'
          ? {
              code: x.code,
              nameZh: x.nameZh ?? undefined,
              nameEn: x.nameEn ?? undefined,
            }
          : {
              code: x.code,
              nameZh: x.nameZh ?? undefined,
              nameEn: x.nameEn ?? undefined,
              operationType: (x as Rule).operationType,
              requiresApproval: (x as Rule).requiresApproval,
              approverRoleId: (x as Rule).approverRoleId ?? undefined,
              priority: (x as Rule).priority,
            },
    );
    setEditing(x);
    setKind(next);
  };
  const isRole = kind === 'role';
  const isRule = kind === 'rule';
  const requires = Form.useWatch('requiresApproval', form);
  const userStatusEnum: Record<string, { text: string }> = {
    ACTIVE: { text: t('admin.userStatus.ACTIVE') },
    INACTIVE: { text: t('admin.userStatus.INACTIVE') },
    LOCKED: { text: t('admin.userStatus.LOCKED') },
  };
  const groupValueEnum = Object.fromEntries(
    groups.map((group) => [group.id, { text: groupLabel(group) }]),
  );
  const userColumns: ProColumns<User>[] = [
    { title: t('admin.username'), dataIndex: 'username', width: 180 },
    { title: t('admin.email'), dataIndex: 'email', width: 200, search: false },
    {
      title: t('admin.groups'),
      dataIndex: 'groupId',
      width: 240,
      valueType: 'select',
      valueEnum: groupValueEnum,
      render: (_, user) =>
        user.groupIds
          .map((groupId) => {
            const group = groups.find((item) => item.id === groupId);
            return group ? groupLabel(group) : groupId;
          })
          .join('、') || '-',
    },
    {
      title: t('admin.status'),
      dataIndex: 'status',
      width: 120,
      valueType: 'select',
      valueEnum: userStatusEnum,
      render: (_, user) => (
        <Tag color={user.status === 'ACTIVE' ? 'green' : 'default'}>
          {userStatusEnum[user.status]?.text ?? user.status}
        </Tag>
      ),
    },
    {
      title: t('admin.registeredAt'),
      dataIndex: 'createdAt',
      width: 180,
      search: false,
      render: (_, user) =>
        user.createdAt ? new Date(user.createdAt).toLocaleString() : '-',
    },
    {
      title: t('admin.lastLoginAt'),
      dataIndex: 'lastLoginAt',
      width: 180,
      search: false,
      render: (_, user) =>
        user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : '-',
    },
    {
      title: t('admin.actions'),
      key: 'actions',
      width: 280,
      search: false,
      fixed: 'right',
      render: (_, user) => (
        <Space size={2} wrap>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setSelectedUserForGroups(user);
              setSelectedUserGroupId(user.groupId ?? user.groupIds[0] ?? null);
            }}
          >
            {t('admin.assignGroups')}
          </Button>
          <Button
            size="small"
            type="link"
            onClick={() => {
              setSelectedUser(user);
              userForm.resetFields();
              setUserModal('reset');
            }}
          >
            {t('admin.resetPassword')}
          </Button>
          <Button
            size="small"
            type="link"
            disabled={user.status === 'LOCKED'}
            danger={user.status === 'ACTIVE'}
            onClick={() => void updateStatus(user)}
          >
            {user.status === 'ACTIVE'
              ? t('admin.deactivate')
              : t('admin.activate')}
          </Button>
        </Space>
      ),
    },
  ];
  const requestUsers = (params: Record<string, unknown>) =>
    Promise.resolve().then(() => {
      const username =
        typeof params.username === 'string'
          ? params.username.trim().toLowerCase()
          : '';
      const groupId =
        typeof params.groupId === 'string' ? params.groupId.trim() : '';
      const status =
        typeof params.status === 'string' ? params.status.trim() : '';
      const filtered = users.filter(
        (user) =>
          (!username || user.username.toLowerCase().includes(username)) &&
          (!groupId || user.groupIds.includes(groupId)) &&
          (!status || user.status === status),
      );
      const page = Math.max(1, Number(params.current ?? 1));
      const pageSize = Math.min(100, Number(params.pageSize ?? 10) || 10);
      const start = (page - 1) * pageSize;
      return {
        data: filtered.slice(start, start + pageSize),
        total: filtered.length,
        success: true,
      };
    });
  const permissionMatrixColumns = [
    {
      title: t('admin.permissionModule'),
      dataIndex: 'module',
      fixed: 'left' as const,
      width: 120,
      render: (value: string) => permissionModuleLabel(value),
    },
    {
      title: t('admin.permissionCapability'),
      fixed: 'left' as const,
      width: 220,
      render: (_: unknown, row: Permission) => permissionLabel(row),
    },
    ...groups.map((group) => ({
      title: (
        <Space orientation="vertical" size={2}>
          <span>{groupLabel(group)}</span>
          {group.isSystem && <Tag color="blue">{t('admin.system')}</Tag>}
        </Space>
      ),
      align: 'center' as const,
      width: 140,
      render: (_: unknown, row: Permission) =>
        (() => {
          const configurable =
            row.assignableGroupCodes === null ||
            row.assignableGroupCodes.includes(group.code);
          return (
            <Tooltip
              title={
                configurable ? undefined : t('admin.permissionNotConfigurable')
              }
            >
              <span
                className={
                  configurable
                    ? undefined
                    : 'permission-matrix-checkbox--restricted'
                }
              >
                <Checkbox
                  aria-label={`${permissionLabel(row)} ${groupLabel(group)}`}
                  checked={(groupPermissionDrafts[group.id] ?? []).includes(
                    row.code,
                  )}
                  disabled={!configurable}
                  onChange={(event) =>
                    toggleGroupPermission(
                      group.id,
                      row.code,
                      event.target.checked,
                    )
                  }
                />
              </span>
            </Tooltip>
          );
        })(),
    })),
  ];
  const renderSettingsActions = () => (
    <Space>
      <Button
        onClick={() =>
          generalSettings && generalForm.setFieldsValue(generalSettings)
        }
      >
        {t('admin.reset')}
      </Button>
      <Button type="primary" onClick={() => void generalForm.submit()}>
        {t('common.save')}
      </Button>
    </Space>
  );
  return (
    <section>
      {holder}
      {view === 'system' ? (
        <Form
          form={generalForm}
          layout="horizontal"
          labelCol={{ span: 5 }}
          wrapperCol={{ span: 19 }}
          labelAlign="left"
          labelWrap
          colon={false}
          data-testid="general-settings-form"
          onFinish={(values) => void saveGeneralSettings(values)}
        >
          <Tabs
            tabPosition="left"
            activeKey={systemTab}
            onChange={changeSystemTab}
            items={[
              {
                key: 'general',
                label: t('admin.generalSettings'),
                forceRender: true,
                children: (
                  <>
                    <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Card
                        title={t('admin.basicSettings')}
                      >
                        <Form.Item name={['tenant', 'version']} hidden>
                          <Input />
                        </Form.Item>
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'code']}
                              label={t('admin.tenantCode')}
                              extra={t('admin.tenantCodeHint')}
                              rules={[
                                { required: true },
                                {
                                  validator: (_, value: string) => {
                                    if (
                                      value === generalSettings?.tenant.code ||
                                      /^[A-Za-z][A-Za-z0-9_-]{0,49}$/.test(value)
                                    ) return Promise.resolve();
                                    return Promise.reject(new Error(t('admin.tenantCodeFormat')));
                                  },
                                },
                              ]}
                            >
                              <Input maxLength={50} />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'nameZh']}
                              label={t('admin.labNameZh')}
                              rules={[{ required: true }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'nameEn']}
                              label={t('admin.labNameEn')}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item label={t('admin.logoUrl')} extra={t('admin.logoUrlHint')}>
                              <Space align="center">
                                {generalSettings?.tenant.logoUrl && (
                                  <img
                                    src={generalSettings.tenant.logoUrl}
                                    alt={t('admin.logoUrl')}
                                    className="admin-logo-preview"
                                  />
                                )}
                                <input
                                  ref={logoInputRef}
                                  type="file"
                                  accept="image/png,image/jpeg"
                                  hidden
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.target.value = '';
                                    if (file) void saveLogo(file);
                                  }}
                                />
                                <Button
                                  icon={<UploadOutlined />}
                                  loading={logoSaving}
                                  disabled={!generalSettings}
                                  onClick={() => logoInputRef.current?.click()}
                                >
                                  {t('admin.logoUpload')}
                                </Button>
                                {generalSettings?.tenant.logoUrl && (
                                  <Button
                                    danger
                                    disabled={logoSaving}
                                    onClick={() => void saveLogo()}
                                  >
                                    {t('admin.logoRemove')}
                                  </Button>
                                )}
                              </Space>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'systemNameZh']}
                              label={t('admin.systemDisplayNameZh')}
                              rules={[{ required: true }]}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'systemNameEn']}
                              label={t('admin.systemDisplayNameEn')}
                              extra={t('admin.systemDisplayNameEnHint')}
                            >
                              <Input />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'domainName']}
                              label={t('admin.siteDomain')}
                              extra={t('admin.siteDomainHint')}
                            >
                              <Input
                                placeholder={t('admin.siteDomainPlaceholder')}
                                inputMode="url"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'defaultLocale']}
                              label={t('admin.defaultLocale')}
                              rules={[{ required: true }]}
                            >
                              <Select
                                options={[
                                  { value: 'zh-CN', label: '中文' },
                                  { value: 'en-US', label: 'English' },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'timezone']}
                              label={t('admin.timezone')}
                              rules={[{ required: true }]}
                            >
                              <Select
                                showSearch
                                options={[
                                  {
                                    value: 'Asia/Shanghai',
                                    label: 'Asia/Shanghai',
                                  },
                                  { value: 'UTC', label: 'UTC' },
                                  {
                                    value: 'America/Los_Angeles',
                                    label: 'America/Los_Angeles',
                                  },
                                  {
                                    value: 'America/New_York',
                                    label: 'America/New_York',
                                  },
                                  {
                                    value: 'Europe/London',
                                    label: 'Europe/London',
                                  },
                                ]}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['tenant', 'contactEmail']}
                              label={t('admin.contactEmail')}
                              rules={[{ type: 'email' }]}
                            >
                              <Input type="email" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                    <Col span={24}>
                      <Card title={t('admin.notificationSettings')}>
                        <Form.Item
                          name={['notificationSettings', 'inAppEnabled']}
                          valuePropName="checked"
                          label={t('admin.inAppNotification')}
                        >
                          <Checkbox>
                            {t('admin.inAppNotificationEnabled')}
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'emailEnabled']}
                          valuePropName="checked"
                          label={t('admin.emailNotification')}
                        >
                          <Checkbox>
                            {t('admin.emailNotificationEnabled')}
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'retentionDays']}
                          label={t('admin.notificationRetentionDays')}
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} max={3650} style={{ width: '100%' }} />
                        </Form.Item>
                      </Card>
                    </Col>
                    <Col span={24}>
                      <Card title={t('admin.securityPolicy')}>
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item
                              name={['securityPolicy', 'passwordMinLength']}
                              label={t('admin.passwordMinLength')}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={8}
                                max={64}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[
                                'securityPolicy',
                                'loginMaxFailedAttempts',
                              ]}
                              label={t('admin.loginMaxFailedAttempts')}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={1}
                                max={20}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['securityPolicy', 'loginLockMinutes']}
                              label={t('admin.loginLockMinutes')}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={1}
                                max={1440}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['securityPolicy', 'loginRateLimitPerMinute']}
                              label={t('admin.loginRateLimitPerMinute')}
                              extra={t('admin.authRateLimitHint')}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={1}
                                max={120}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[
                                'securityPolicy',
                                'passwordResetRequestRateLimitPerMinute',
                              ]}
                              label={t(
                                'admin.passwordResetRequestRateLimitPerMinute',
                              )}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={1}
                                max={60}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[
                                'securityPolicy',
                                'passwordResetConfirmRateLimitPerMinute',
                              ]}
                              label={t(
                                'admin.passwordResetConfirmRateLimitPerMinute',
                              )}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={1}
                                max={60}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={['securityPolicy', 'sessionLifetimeHours']}
                              label={t('admin.sessionLifetimeHours')}
                              rules={[{ required: true }]}
                            >
                              <InputNumber
                                min={1}
                                max={2160}
                                style={{ width: '100%' }}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[
                                'securityPolicy',
                                'initialPasswordMustChange',
                              ]}
                              valuePropName="checked"
                              label={t('admin.initialPasswordPolicy')}
                            >
                              <Checkbox>
                                {t('admin.initialPasswordMustChange')}
                              </Checkbox>
                            </Form.Item>
                          </Col>
                        </Row>
                      </Card>
                    </Col>
                    </Row>
                    <div className="admin-settings-footer">
                      {renderSettingsActions()}
                    </div>
                  </>
                ),
              },
              {
                key: 'email',
                label: t('admin.emailSettings'),
                forceRender: true,
                children: (
                  <>
                    <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Card title={t('admin.smtpSettings')}>
                        <Form.Item
                          name={['notificationSettings', 'email', 'host']}
                          label={t('admin.smtpHost')}
                          extra={t('admin.smtpHostHint')}
                        >
                          <Input placeholder="smtp.example.com" />
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'port']}
                          label={t('admin.smtpPort')}
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={1} max={65535} style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'secure']}
                          valuePropName="checked"
                          label={t('admin.smtpSecure')}
                        >
                          <Checkbox
                            onChange={(event) => {
                              if (event.target.checked) {
                                generalForm.setFieldValue(
                                  ['notificationSettings', 'email', 'requireTls'],
                                  false,
                                );
                              }
                            }}
                          >
                            {t('admin.smtpSecureHint')}
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'requireTls']}
                          valuePropName="checked"
                          label={t('admin.smtpRequireTls')}
                        >
                          <Checkbox
                            onChange={(event) => {
                              if (event.target.checked) {
                                generalForm.setFieldValue(
                                  ['notificationSettings', 'email', 'secure'],
                                  false,
                                );
                              }
                            }}
                          >
                            {t('admin.smtpRequireTlsHint')}
                          </Checkbox>
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'username']}
                          label={t('admin.smtpUsername')}
                        >
                          <Input autoComplete="off" />
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'password']}
                          label={t('admin.smtpPassword')}
                          extra={t('admin.smtpPasswordHint')}
                        >
                          <Input.Password autoComplete="new-password" />
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'fromEmail']}
                          label={t('admin.smtpFromEmail')}
                          rules={[{ type: 'email' }]}
                        >
                          <Input type="email" />
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'fromName']}
                          label={t('admin.smtpFromName')}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name={['notificationSettings', 'email', 'replyTo']}
                          label={t('admin.smtpReplyTo')}
                          rules={[{ type: 'email' }]}
                        >
                          <Input type="email" />
                        </Form.Item>
                        <Form.Item
                          label={t('admin.testEmailRecipient')}
                          extra={t('admin.testEmailRecipientHint')}
                        >
                          <Input
                            type="email"
                            value={testEmailRecipient}
                            onChange={(event) =>
                              setTestEmailRecipient(event.target.value)
                            }
                            placeholder={t('admin.testEmailRecipientPlaceholder')}
                          />
                        </Form.Item>
                        <Form.Item wrapperCol={{ span: 19, offset: 5 }}>
                          <Button
                            onClick={() => void testEmail()}
                            loading={testEmailLoading}
                          >
                            {t('admin.testEmail')}
                          </Button>
                        </Form.Item>
                      </Card>
                    </Col>
                    </Row>
                    <div className="admin-settings-footer">
                      {renderSettingsActions()}
                    </div>
                  </>
                ),
              },
              {
                key: 'business',
                label: t('admin.businessSettings'),
                forceRender: true,
                children: (
                  <>
                    <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Card
                        title={t('admin.basicSettings')}
                      >
                        <Row gutter={16}>
                          <Col span={24}>
                            <Form.Item
                              name={['businessDefaults', 'defaultSampleStatus']}
                              label={t('admin.defaultSampleStatus')}
                              layout="horizontal"
                              labelAlign="left"
                              labelCol={{ span: 5 }}
                              wrapperCol={{ span: 19 }}
                              rules={[{ required: true }]}
                            >
                              <Select
                                aria-label={t('admin.defaultSampleStatus')}
                                options={defaultSampleStatuses.map((value) => ({
                                  value,
                                  label: t(`samples.statusOptions.${value}`, {
                                    defaultValue: value,
                                  }),
                                }))}
                              />
                            </Form.Item>
                          </Col>
                          {canManageStorage && (
                            <>
                              <Col span={24}>
                                <Form.Item
                                  name={[
                                    'businessDefaults',
                                    'defaultStorageBoxSpecId',
                                  ]}
                                  label={t('admin.defaultStorageBoxSpec')}
                                  layout="horizontal"
                                  labelAlign="left"
                                  labelCol={{ span: 5 }}
                                  wrapperCol={{ span: 19 }}
                                  rules={[{ required: true }]}
                                >
                                  <Select
                                    aria-label={t(
                                      'admin.defaultStorageBoxSpec',
                                    )}
                                    options={boxSpecs.map((spec) => ({
                                      value: spec.id,
                                      label: boxSpecLabel(spec),
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                              <Col span={24}>
                                <Form.Item
                                  name={[
                                    'businessDefaults',
                                    'defaultStorageDeviceSpecId',
                                  ]}
                                  label={t('admin.defaultStorageDeviceSpec')}
                                  layout="horizontal"
                                  labelAlign="left"
                                  labelCol={{ span: 5 }}
                                  wrapperCol={{ span: 19 }}
                                >
                                  <Select
                                    allowClear
                                    aria-label={t(
                                      'admin.defaultStorageDeviceSpec',
                                    )}
                                    options={deviceSpecs.map((spec) => ({
                                      value: spec.id,
                                      label: deviceSpecLabel(spec),
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                            </>
                          )}
                          <Col span={24}>
                            <Form.Item
                              name={[
                                'businessDefaults',
                                'defaultApprovalRulesEnabled',
                              ]}
                              valuePropName="checked"
                              label={t('admin.enableApproval')}
                              layout="horizontal"
                              labelAlign="left"
                              labelCol={{ span: 5 }}
                              wrapperCol={{ span: 19 }}
                            >
                              <Switch
                                checkedChildren={t('common.enabled')}
                                unCheckedChildren={t('common.disabled')}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item
                              name={[
                                'maintenanceSettings',
                                'liquidNitrogenForecastEnabled',
                              ]}
                              valuePropName="checked"
                              label={t('admin.enableLiquidNitrogenForecast')}
                              extra={t('admin.liquidNitrogenForecastHint')}
                            >
                              <Switch
                                disabled={!canManageIdentity}
                                checkedChildren={t('common.enabled')}
                                unCheckedChildren={t('common.disabled')}
                              />
                            </Form.Item>
                          </Col>
                          {canReadQuality && (
                            <Col span={24}>
                              <Form.Item
                                label={t('admin.enableQcTemplate')}
                                layout="horizontal"
                                labelAlign="left"
                                labelCol={{ span: 5 }}
                                wrapperCol={{ span: 19 }}
                                extra={t('quality.templateFeatureHint')}
                              >
                                <Switch
                                  checked={templatesEnabled}
                                  loading={templateSettingLoading}
                                  disabled={
                                    !currentUser?.permissions.includes(
                                      'qc:manage',
                                    )
                                  }
                                  checkedChildren={t('common.enabled')}
                                  unCheckedChildren={t('common.disabled')}
                                  onChange={(checked) =>
                                    void updateTemplateSetting(checked)
                                  }
                                />
                              </Form.Item>
                            </Col>
                          )}
                        </Row>
                      </Card>
                    </Col>
                    <Col span={24}>
                      <Card
                        title={t('admin.cellLineCatalogSettings')}
                        extra={
                          <Tooltip title={t('admin.catalogSourcePriorityHint')}>
                            <QuestionCircleOutlined />
                          </Tooltip>
                        }
                      >
                        <Form.Item label={t('admin.catalogSourcePriority')}>
                          <Select
                            style={{ width: 240 }}
                            value={catalogPrimarySource}
                            options={CATALOG_SOURCE_BANK_OPTIONS}
                            onChange={(value) => {
                              if (!value) return;
                              setCatalogPrimarySource(value);
                              if (catalogFallbackSource === value) {
                                setCatalogFallbackSource(catalogPrimarySource);
                              }
                            }}
                          />
                        </Form.Item>
                        <Form.Item label={t('admin.catalogSourceFallback')}>
                          <Select
                            style={{ width: 240 }}
                            value={catalogFallbackSource}
                            options={CATALOG_SOURCE_BANK_OPTIONS.filter(
                              (option) => option.value !== catalogPrimarySource,
                            )}
                            onChange={(value) => {
                              if (value) {
                                setCatalogFallbackSource(value);
                              }
                            }}
                          />
                        </Form.Item>
                        <Alert
                          type="warning"
                          showIcon
                          message={t('admin.catalogSourcePriorityNotice')}
                        />
                      </Card>
                    </Col>
                    <Col span={24}>
                      <Card
                        title={t('admin.inventoryOperationSettings')}
                      >
                        <Table
                          size="small"
                          rowKey={(value) => value}
                          tableLayout="fixed"
                          scroll={{ x: 'max-content' }}
                          pagination={false}
                          dataSource={inventoryOperations}
                          columns={[
                            {
                              title: t('admin.operation'),
                              render: (_: unknown, value: string) =>
                                t(`admin.operationTypes.${value}`, {
                                  defaultValue: value,
                                }),
                            },
                            {
                              title: t('admin.inventoryOperationEnabled'),
                              render: (_: unknown, value: string) => (
                                <Form.Item
                                  name={[
                                    'inventoryOperationSettings',
                                    value,
                                    'enabled',
                                  ]}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox />
                                </Form.Item>
                              ),
                            },
                            {
                              title: t('admin.inventoryOperationUserVisible'),
                              render: (_: unknown, value: string) => (
                                <Form.Item
                                  name={[
                                    'inventoryOperationSettings',
                                    value,
                                    'userVisible',
                                  ]}
                                  valuePropName="checked"
                                  style={{ marginBottom: 0 }}
                                >
                                  <Checkbox
                                    disabled={
                                      !['PUTAWAY', 'CONSUME'].includes(value)
                                    }
                                  />
                                </Form.Item>
                              ),
                            },
                          ]}
                        />
                      </Card>
                    </Col>
                    <Col span={24}>
                      <Card
                        title={t('admin.numberingRules')}
                      >
                        <Table
                          size="small"
                          rowKey="entityType"
                          tableLayout="fixed"
                          scroll={{ x: 'max-content' }}
                          pagination={false}
                          dataSource={generalSettings?.numberingRules ?? []}
                          columns={[
                            {
                              title: t('admin.numberingObject'),
                              render: (_, row: NumberingRule, index) => (
                                <>
                                  <Form.Item
                                    name={[
                                      'numberingRules',
                                      index,
                                      'entityType',
                                    ]}
                                    hidden
                                  >
                                    <Input />
                                  </Form.Item>
                                  <Form.Item
                                    name={['numberingRules', index, 'version']}
                                    hidden
                                  >
                                    <Input />
                                  </Form.Item>
                                  {numberingEntityTypeLabel(row.entityType)}
                                </>
                              ),
                            },
                            {
                              title: t('admin.numberingPrefix'),
                              render: (_, _row, index) => (
                                <Form.Item
                                  name={['numberingRules', index, 'prefix']}
                                  rules={[{ required: true }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Input />
                                </Form.Item>
                              ),
                            },
                            {
                              title: t('admin.numberingDateFormat'),
                              render: (_, _row, index) => (
                                <Form.Item
                                  name={['numberingRules', index, 'dateFormat']}
                                  rules={[{ required: true }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <Select
                                    options={numberingDateFormats.map(
                                      (value) => ({
                                        value,
                                        label: t(
                                          `admin.numberingDateFormats.${value}`,
                                          { defaultValue: value },
                                        ),
                                      }),
                                    )}
                                  />
                                </Form.Item>
                              ),
                            },
                            {
                              title: t('admin.serialLength'),
                              render: (_, _row, index) => (
                                <Form.Item
                                  name={[
                                    'numberingRules',
                                    index,
                                    'serialLength',
                                  ]}
                                  rules={[{ required: true }]}
                                  style={{ marginBottom: 0 }}
                                >
                                  <InputNumber
                                    min={3}
                                    max={8}
                                    style={{ width: '100%' }}
                                  />
                                </Form.Item>
                              ),
                            },
                          ]}
                        />
                      </Card>
                    </Col>
                    {canManageStorage && (
                      <>
                        <Col span={24}>
                          <StorageDeviceSpecsPage />
                        </Col>
                        <Col span={24}>
                          <StorageBoxSpecsPage />
                        </Col>
                      </>
                    )}
                    {canReadQuality && (
                      <Col span={24}>
                        <QualitySettingsPage
                          templatesEnabled={templatesEnabled}
                        />
                      </Col>
                    )}
                  </Row>
                  <div className="admin-settings-footer">
                    {renderSettingsActions()}
                  </div>
                  </>
                ),
              },
              {
                key: 'groups-permissions',
                label: t('admin.groupPermissionSettings'),
                forceRender: true,
                children: (
                  <>
                    <Row gutter={[16, 16]}>
                      <Col span={24}>
                        <Card title={t('admin.newUserDefaults')}>
                          <Form.Item
                            name={['businessDefaults', 'defaultUserGroupId']}
                            label={t('admin.defaultUserGroup')}
                            layout="horizontal"
                            labelAlign="left"
                            labelCol={{ span: 5 }}
                            wrapperCol={{ span: 19 }}
                            rules={[{ required: true }]}
                          >
                            <Select
                              aria-label={t('admin.defaultUserGroup')}
                              options={groups.map((group) => ({
                                value: group.id,
                                label: groupLabel(group),
                              }))}
                            />
                          </Form.Item>
                        </Card>
                      </Col>
                    <Col span={24}>
                      <Card
                        title={t('admin.groups')}
                        extra={
                          <Space>
                            <Button
                              onClick={() => setPermissionMatrixOpen(true)}
                            >
                              {t('admin.configurePermissionMatrix')}
                            </Button>
                            <Button
                              type="link"
                              icon={<PlusOutlined />}
                              onClick={() => open('group')}
                            >
                              {t('admin.createGroup')}
                            </Button>
                            <Tooltip title={t('admin.refresh')}>
                              <Button
                                aria-label={t('admin.refresh')}
                                type="text"
                                shape="circle"
                                icon={<ReloadOutlined />}
                                loading={loading}
                                onClick={() => void load()}
                              />
                            </Tooltip>
                          </Space>
                        }
                      >
                        <Table
                          size="small"
                          rowKey="id"
                          tableLayout="fixed"
                          scroll={{ x: 'max-content' }}
                          pagination={false}
                          dataSource={groups}
                          columns={[
                            { title: t('admin.code'), dataIndex: 'code' },
                            {
                              title: t('admin.name'),
                              render: (_, x: Group) => (
                                <Space>
                                  <span>{title(x)}</span>
                                  {x.isSystem && (
                                    <Tag color="blue">{t('admin.system')}</Tag>
                                  )}
                                </Space>
                              ),
                            },
                            {
                              title: t('admin.members'),
                              dataIndex: 'memberCount',
                            },
                            {
                              title: t('admin.groupPermissionCount'),
                              render: (_, x: Group) =>
                                x.permissionCodes.length > 0
                                  ? x.permissionCodes.length
                                  : '-',
                            },
                            {
                              title: t('admin.actions'),
                              render: (_, x: Group) => (
                                <Button onClick={() => edit(x, 'group')}>
                                  {t('admin.edit')}
                                </Button>
                              ),
                            },
                          ]}
                        />
                      </Card>
                    </Col>
                    </Row>
                    <div className="admin-settings-footer">
                      {renderSettingsActions()}
                    </div>
                  </>
                ),
              },
              {
                key: 'approval-rules',
                label: t('admin.rules'),
                forceRender: true,
                children: (
                  <Card
                    title={t('admin.rules')}
                    extra={
                      <Space>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={() => open('rule')}
                        >
                          {t('admin.createRule')}
                        </Button>
                        <Tooltip title={t('admin.refresh')}>
                          <Button
                            aria-label={t('admin.refresh')}
                            type="text"
                            shape="circle"
                            icon={<ReloadOutlined />}
                            loading={loading}
                            onClick={() => void load()}
                          />
                        </Tooltip>
                      </Space>
                    }
                  >
                    <Table
                      size="small"
                      rowKey="id"
                      tableLayout="fixed"
                      scroll={{ x: 'max-content' }}
                      pagination={false}
                      dataSource={rules}
                      columns={[
                        { title: t('admin.code'), dataIndex: 'code' },
                        {
                          title: t('admin.operation'),
                          dataIndex: 'operationType',
                          render: (value: string) =>
                            t(`admin.operationTypes.${value}`, {
                              defaultValue: value,
                            }),
                        },
                        {
                          title: t('admin.actions'),
                          render: (_, x: Rule) => (
                            <Button onClick={() => edit(x, 'rule')}>
                              {t('admin.edit')}
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </Card>
                ),
              },
              {
                key: 'status',
                label: t('nav.system'),
                forceRender: true,
                children: <SystemStatusPage />,
              },
            ]}
          />
        </Form>
      ) : (
        <ProTable<User>
          headerTitle={t('admin.users')}
          rowKey="id"
          className="users-table"
          actionRef={usersActionRef}
          columns={userColumns}
          request={requestUsers}
          search={{ labelWidth: 'auto', defaultCollapsed: false }}
          cardBordered
          options={{ reload: true, density: true, setting: true, fullScreen: true }}
          defaultSize="small"
          rowSelection={{
            selectedRowKeys: selectedUserIds,
            onChange: (keys) => setSelectedUserIds(keys.map(String)),
          }}
          tableAlertOptionRender={(props) =>
            selectedUserIds.length > 0 ? (
              <Space size="small">
                <Popconfirm
                  title={t('admin.confirmDeleteUsers', {
                    count: selectedUserIds.length,
                  })}
                  onConfirm={() => void deleteSelectedUsers()}
                >
                  <Button type="link" danger icon={<DeleteOutlined />}>
                    {t('admin.deleteSelectedUsers', {
                      count: selectedUserIds.length,
                    })}
                  </Button>
                </Popconfirm>
                <Button type="link" onClick={props.onCleanSelected}>
                  {t('common.clearSelection')}
                </Button>
              </Space>
            ) : null
          }
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
          toolBarRender={() => [
            <Button
              key="create"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                userForm.resetFields();
                setUserModal('create');
              }}
            >
              {t('admin.createUser')}
            </Button>,
          ]}
        />
      )}
      <Modal
        open={!!kind}
        title={
          editing
            ? t('admin.edit')
            : isRole
              ? t('admin.createRole')
              : kind === 'group'
                ? t('admin.createGroup')
                : t('admin.createRule')
        }
        onCancel={() => {
          setKind(undefined);
          setEditing(undefined);
        }}
        onOk={() => void form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void save(values)}
        >
          <Form.Item
            name="code"
            label={t('admin.code')}
            rules={[{ required: true }]}
          >
            <Input disabled={!!editing} />
          </Form.Item>
          <Form.Item
            name="nameZh"
            label={t('admin.nameZh')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="nameEn" label={t('admin.nameEn')}>
            <Input />
          </Form.Item>
          {isRole && (
            <Form.Item name="permissionCodes" label={t('admin.permissions')}>
              <Input />
            </Form.Item>
          )}
          {isRule && (
            <>
              <Form.Item
                name="operationType"
                label={t('admin.operation')}
                rules={[{ required: true }]}
              >
                <Select
                  disabled={!!editing}
                  options={operations.map((value) => ({
                    value,
                    label: t(`admin.operationTypes.${value}`, {
                      defaultValue: value,
                    }),
                  }))}
                />
              </Form.Item>
              <Form.Item name="requiresApproval" valuePropName="checked">
                <Checkbox>{t('admin.requiresApproval')}</Checkbox>
              </Form.Item>
              {requires && (
                <Form.Item
                  name="approverRoleId"
                  label={t('admin.approverRole')}
                  rules={[{ required: true }]}
                >
                  <Select
                    options={roles.map((x) => ({
                      value: x.id,
                      label: roleLabel(x),
                    }))}
                  />
                </Form.Item>
              )}
              <Form.Item name="priority" label={t('admin.priority')}>
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
      <Modal
        open={!!userModal}
        title={
          userModal === 'create'
            ? t('admin.createUser')
            : t('admin.resetPassword')
        }
        onCancel={() => setUserModal(undefined)}
        onOk={() => void userForm.submit()}
        confirmLoading={savingUser}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={(values) => void saveUser(values)}
        >
          {userModal === 'create' && (
            <>
              <Form.Item name="username" label={t('admin.username')}>
                <Input autoComplete="off" />
              </Form.Item>
              <Form.Item
                name="email"
                label={t('admin.email')}
                rules={[{ required: true, type: 'email' }]}
              >
                <Input type="email" />
              </Form.Item>
            </>
          )}
          <Form.Item label={t('admin.password')} required>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="password"
                noStyle
                rules={[
                  {
                    required: true,
                    min: passwordMinLength,
                    message: t('admin.passwordRule', {
                      min: passwordMinLength,
                    }),
                  },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Button onClick={generateInitialPassword}>
                {t('admin.generatePassword')}
              </Button>
            </Space.Compact>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={!!createdUser}
        title={t('admin.userCreatedTitle')}
        onCancel={() => setCreatedUser(undefined)}
        maskClosable={false}
        keyboard={false}
        width={560}
        footer={[
          <Button key="close" onClick={() => setCreatedUser(undefined)}>
            {t('common.close')}
          </Button>,
          <Button
            key="copy"
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => void copyCreatedUserInfo()}
          >
            {t('admin.copyUserInfo')}
          </Button>,
        ]}
      >
        <Alert
          type="success"
          showIcon
          message={t('admin.userCreatedHint')}
          style={{ marginBottom: 20 }}
        />
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={t('admin.username')}>
            {createdUser?.username}
          </Descriptions.Item>
          <Descriptions.Item label={t('admin.email')}>
            {createdUser?.email}
          </Descriptions.Item>
          <Descriptions.Item label={t('admin.password')}>
            <Input.Password
              value={createdUser?.password ?? ''}
              readOnly
              autoComplete="off"
            />
          </Descriptions.Item>
        </Descriptions>
        <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
          {t('admin.userCreatedSecurityHint')}
        </Typography.Paragraph>
      </Modal>
      <Modal
        open={!!selectedUserForGroups}
        title={t('admin.assignGroups')}
        onCancel={() => setSelectedUserForGroups(undefined)}
        onOk={() => void saveUserGroups()}
      >
        <Form layout="vertical">
          <Form.Item label={t('admin.groups')}>
            <Select
              aria-label="User groups"
              value={selectedUserGroupId}
              onChange={setSelectedUserGroupId}
              options={groups.map((group) => ({
                value: group.id,
                label: groupLabel(group),
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={permissionMatrixOpen}
        title={t('admin.permissionMatrix')}
        onCancel={closePermissionMatrix}
        footer={[
          <Button key="cancel" onClick={closePermissionMatrix}>
            {t('common.cancel')}
          </Button>,
          <Button
            key="reset"
            disabled={!hasPermissionMatrixChanges}
            onClick={resetPermissionMatrix}
          >
            {t('admin.resetPermissionMatrix')}
          </Button>,
          <Button
            key="save"
            type="primary"
            disabled={!hasPermissionMatrixChanges}
            onClick={() => void savePermissionMatrix()}
          >
            {t('admin.savePermissionMatrix')}
          </Button>,
        ]}
        width={980}
      >
        <Alert
          type="info"
          showIcon
          message={t('admin.permissionMatrixReadonlyHint')}
          description={t('admin.inventoryPermissionMatrixHint')}
          style={{ marginBottom: 12 }}
        />
        <Table
          size="small"
          rowKey="code"
          pagination={false}
          scroll={{ x: 340 + groups.length * 140, y: 520 }}
          dataSource={permissions}
          columns={permissionMatrixColumns}
        />
      </Modal>
    </section>
  );
}
